import { AiDisclaimer } from './ai-disclaimer';
import { PrototypeAiRenderEditor } from './ai-render-editor';
import type { AiRenderSettings } from './ai-render-settings';
import { AiRenderUrl, DISCLAIMER_TRANSLATIONS, EMPTY_RENDER_SETTINGS } from './ai-render-settings';
import { CanvasPage } from './canvas-page';
import { PrototypePanel } from './panel/prototype-panel';
import { PrototypeRender } from './panel/prototype-render';
import { PrototypeUrlState } from './panel/prototype-url-state';
import { PrototypeBarOffset } from './prototype-bar-offset';
import type { PrototypeState } from './prototype-state';
import { MAX_CONTENT, STATE_OPTIONS } from './prototype-state';

const urlState = new PrototypeUrlState(STATE_OPTIONS);

/** The design page: keeps the state in the URL and applies it to the markup inside `[data-prototype-root]`. */
class PrototypePage {
    private readonly root: HTMLElement;
    private readonly panel: PrototypePanel<typeof STATE_OPTIONS> | null;
    private readonly canvas: CanvasPage;
    private readonly renderEditor: PrototypeAiRenderEditor | null;
    private readonly disclaimerText: string;
    private state: PrototypeState;
    private renders: AiRenderSettings;
    private beforeMaxContent: PrototypeState | null = null;

    constructor(root: HTMLElement) {
        this.root = root;
        this.state = urlState.read(window.location.search);
        this.renders = AiRenderUrl.read(window.location.search);
        this.disclaimerText = AiDisclaimer.defaultText(root);
        this.canvas = new CanvasPage(root, changed => this.setState({ ...this.state, ...changed }));

        const panelElement = document.querySelector<HTMLElement>('[data-prototype-panel]');
        // embed=1 is how the developer guide frames the page: the design alone, without the bar.
        if (new URLSearchParams(window.location.search).get('embed') === '1') {
            panelElement?.remove();
        }

        this.panel = panelElement?.isConnected
            ? new PrototypePanel(panelElement, urlState, this.state, {
                onChange: state => this.setState(state),
                onReset: () => {
                    this.renders = EMPTY_RENDER_SETTINGS;
                    this.setState(urlState.defaults());
                },
                onMaxContent: () => this.toggleMaxContent(),
            })
            : null;
        if (panelElement?.isConnected) {
            PrototypeBarOffset.bind(panelElement);
        }

        const renderSettings = panelElement?.isConnected ? panelElement.querySelector<HTMLElement>('[data-prototype-render-settings]') : null;
        this.renderEditor = renderSettings
            ? new PrototypeAiRenderEditor(renderSettings, this.renders, this.disclaimerFor(this.state), renders => this.setRenders(renders))
            : null;

        for (const pair of PrototypeRender.unknownConditions(root, urlState)) {
            console.warn(`Prototype: data-prototype-when="${pair}" names no state value, so its element never shows.`);
        }

        this.panel?.setMaxContent(this.isMaxContent(this.state));
        this.render();
    }

    private setState(state: PrototypeState) {
        this.state = state;
        this.panel?.setState(state);
        this.panel?.setMaxContent(this.isMaxContent(state));
        this.writeUrl();
        this.render();
    }

    private setRenders(renders: AiRenderSettings) {
        this.renders = renders;
        this.writeUrl();
        this.render();
    }

    private writeUrl() {
        const search = AiRenderUrl.write(this.renders, urlState.write(this.state, window.location.search));
        window.history.replaceState(null, '', `${window.location.pathname}${search}${window.location.hash}`);
    }

    /** On: remembers the page as it is and shows the preset. Off: goes back to that, or to the design when the page opened on the preset. */
    private toggleMaxContent() {
        if (this.isMaxContent(this.state)) {
            const back = this.beforeMaxContent ?? urlState.defaults();
            this.beforeMaxContent = null;
            this.setState(back);
        } else {
            this.beforeMaxContent = this.state;
            this.setState(urlState.complete(MAX_CONTENT));
        }
    }

    private isMaxContent(state: PrototypeState): boolean {
        return urlState.same(state, urlState.complete(MAX_CONTENT));
    }

    private render() {
        PrototypeRender.apply(this.root, urlState, this.state);
        this.canvas.apply(this.state);
        const disclaimer = this.disclaimerFor(this.state);
        AiDisclaimer.apply(this.root, this.renders, disclaimer);
        this.renderEditor?.setSettings(this.renders, disclaimer, Number(this.state.aiImages));
    }

    private disclaimerFor(state: PrototypeState): string {
        return state.language === 'nl' ? this.disclaimerText : DISCLAIMER_TRANSLATIONS[state.language];
    }
}

const root = document.querySelector<HTMLElement>('[data-prototype-root]');
if (root) {
    new PrototypePage(root);
}
